import React from 'react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Modal } from 'src/components/Modal';
import { SyncSummary } from '../../types/sync.types';

interface SyncSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  summary: SyncSummary;
}

export const SyncSummaryModal: React.FC<SyncSummaryModalProps> = ({
  isOpen,
  onClose,
  summary,
}) => {
  const handleDownloadErrorReport = () => {
    if (summary.errors.length === 0) return;

    const errorReport = summary.errors
      .map(
        (error, index) =>
          `${index + 1}. ${error.recordDescription}: ${error.message}`,
      )
      .join('\n');

    const blob = new Blob([errorReport], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sync-errors-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      title="Sync Summary"
      isOpen={isOpen}
      onClose={onClose}
      className="w-auto min-w-96 max-w-xl"
    >
      <div className="space-y-4">
        {/* New Contacts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" weight="fill" />
            <span className="text-sm">New Contacts</span>
          </div>
          <span className="font-semibold text-lg">{summary.newContacts}</span>
        </div>

        {/* Updated Contacts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" weight="fill" />
            <span className="text-sm">Updated Contacts</span>
          </div>
          <span className="font-semibold text-lg">
            {summary.updatedContacts}
          </span>
        </div>

        {/* Skipped Contacts */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-500" weight="fill" />
            <span className="text-sm">Skipped Contacts</span>
          </div>
          <span className="font-semibold text-lg">
            {summary.skippedContacts}
          </span>
        </div>

        {/* Error Report Download */}
        {summary.hasErrors && summary.errors.length > 0 && (
          <div className="flex items-center gap-2 ml-7">
            <button
              onClick={handleDownloadErrorReport}
              className="text-primary underline hover:text-primary-600 text-sm"
            >
              Download error report
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};
